import axios from 'axios';
import MainURL from '../MainURL';
import { renameFinanceAttachmentFile } from './financeAttachment';

export type FinanceType = 'REVENUE' | 'EXPENSE' | 'CAPITAL';

export type FinanceRecord = {
  id: number;
  type: FinanceType;
  category: string;
  payment_method: string | null;
  amount: number;
  description: string | null;
  transaction_date: string | null;
  cash_flow_date: string | null;
  attachment_path: string | null;
  attachment_name: string | null;
  created_at?: string;
  updated_at?: string;
};

export type FinanceSummaryItem = {
  month: string; // YYYY-MM
  revenue: number;
  expense: number;
  net: number;
};

export type FinanceBalance = {
  capital: number;
  revenue: number;
  expense: number;
  total: number;
};

export type FinanceSummaryResponse = {
  rangeMonths: number;
  data: FinanceSummaryItem[];
  balance?: FinanceBalance;
};

export type CreateFinancePayload = {
  type: FinanceType;
  category: string;
  payment_method: string;
  amount: number;
  description?: string;
  transaction_date?: string | null;
  cash_flow_date?: string | null;
};

export type RecurringExpense = {
  id: number;
  category: string;
  payment_method: string | null;
  amount: number;
  description: string | null;
  repeat_day: number;
  start_date: string | null;
  is_active: 0 | 1;
  created_at?: string;
  updated_at?: string;
};

export type CreateRecurringExpensePayload = {
  category: string;
  payment_method: string;
  amount: number;
  repeat_day: number;
  start_date: string;
  description?: string;
};

export type PatchRecurringExpensePayload = Partial<{
  category: string;
  payment_method: string;
  amount: number;
  repeat_day: number;
  start_date: string;
  description: string | null;
  is_active: 0 | 1;
}>;

type ApiErrorBody = {
  ok?: boolean;
  error?: { code?: string; message?: string };
  message?: string;
};

const API_BASE = `${MainURL}/api/finance`;

function getErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as ApiErrorBody | undefined;
    if (body?.error?.message) return body.error.message;
    if (typeof body?.message === 'string') return body.message;
  }
  return fallback;
}

export type FetchFinanceListOptions = {
  type?: FinanceType;
  month?: string;
};

export async function fetchFinanceList(
  requesterId: number,
  options?: FetchFinanceListOptions,
) {
  try {
    const params: FetchFinanceListOptions & { requesterId: number } = {
      requesterId,
    };
    if (options?.type) params.type = options.type;
    if (options?.month) params.month = options.month;

    const res = await axios.get<{ ok: boolean; data?: FinanceRecord[] }>(`${API_BASE}/list`, {
      params,
    });
    if (!res.data?.ok || !Array.isArray(res.data.data)) {
      throw new Error('재무 목록 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '재무 목록을 불러오지 못했습니다.'));
  }
}

export async function fetchFinanceSummary(requesterId: number, months = 12) {
  try {
    const res = await axios.get<{
      ok: boolean;
      data?: FinanceSummaryItem[];
      rangeMonths?: number;
      balance?: FinanceBalance;
    }>(`${API_BASE}/summary`, { params: { requesterId, months } });
    if (!res.data?.ok || !Array.isArray(res.data.data)) {
      throw new Error('통계 응답 형식이 올바르지 않습니다.');
    }
    return {
      rangeMonths: Number(res.data.rangeMonths || months),
      data: res.data.data,
      balance: res.data.balance,
    } as FinanceSummaryResponse;
  } catch (err) {
    throw new Error(getErrorMessage(err, '월별 통계를 불러오지 못했습니다.'));
  }
}

export function getFinanceAttachmentUrl(attachmentPath: string): string {
  return `${API_BASE}/files/${attachmentPath}`;
}

function buildFinanceFormData(
  requesterId: number,
  payload: CreateFinancePayload,
  file?: File,
  options?: { uploaderEmail?: string }
) {
  const fd = new FormData();
  fd.append('requesterId', String(requesterId));
  fd.append('type', payload.type);
  fd.append('category', payload.category);
  fd.append('payment_method', payload.payment_method);
  fd.append('amount', String(payload.amount));
  if (payload.description != null) fd.append('description', payload.description);
  if (payload.transaction_date != null) fd.append('transaction_date', payload.transaction_date);
  if (payload.cash_flow_date != null) fd.append('cash_flow_date', payload.cash_flow_date);
  if (options?.uploaderEmail) fd.append('uploader_email', options.uploaderEmail);
  if (file) {
    fd.append('attachment_original_name', file.name);
    const uploadFile = options?.uploaderEmail
      ? renameFinanceAttachmentFile(file, options.uploaderEmail)
      : file;
    fd.append('attachment', uploadFile);
  }
  return fd;
}

export async function createFinanceRecord(
  requesterId: number,
  payload: CreateFinancePayload,
  file?: File,
  options?: { uploaderEmail?: string }
) {
  try {
    const res = await axios.post<{ ok: boolean; data?: FinanceRecord }>(
      API_BASE,
      buildFinanceFormData(requesterId, payload, file, options),
      { params: { requesterId } }
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('등록 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '재무 내역 등록에 실패했습니다.'));
  }
}

export async function patchFinanceRecord(
  requesterId: number,
  id: number,
  payload: CreateFinancePayload,
  file?: File,
  options?: { uploaderEmail?: string }
) {
  try {
    const formData = buildFinanceFormData(requesterId, payload, file, options);
    const res = file
      ? await axios.post<{ ok: boolean; data?: FinanceRecord }>(
          `${API_BASE}/${id}/update`,
          formData,
          { params: { requesterId } }
        )
      : await axios.patch<{ ok: boolean; data?: FinanceRecord }>(
          `${API_BASE}/${id}`,
          formData,
          { params: { requesterId } }
        );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('수정 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '재무 내역 수정에 실패했습니다.'));
  }
}

export async function deleteFinanceRecord(requesterId: number, id: number) {
  try {
    const res = await axios.delete<{ ok: boolean; data?: { id: number } }>(`${API_BASE}/${id}`, {
      params: { requesterId },
    });
    if (!res.data?.ok) {
      throw new Error('삭제 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '재무 내역 삭제에 실패했습니다.'));
  }
}

export async function fetchRecurringExpenses(requesterId: number) {
  try {
    const res = await axios.get<{ ok: boolean; data?: RecurringExpense[] }>(`${API_BASE}/recurring`, {
      params: { requesterId },
    });
    if (!res.data?.ok || !Array.isArray(res.data.data)) {
      throw new Error('고정비 목록 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '고정비 목록을 불러오지 못했습니다.'));
  }
}

export async function createRecurringExpense(
  requesterId: number,
  payload: CreateRecurringExpensePayload
) {
  try {
    const res = await axios.post<{ ok: boolean; data?: RecurringExpense }>(`${API_BASE}/recurring`, {
      requesterId,
      ...payload,
    });
    if (!res.data?.ok || !res.data.data) {
      throw new Error('고정비 등록 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '고정비 등록에 실패했습니다.'));
  }
}

export async function patchRecurringExpense(
  requesterId: number,
  id: number,
  payload: PatchRecurringExpensePayload
) {
  try {
    const res = await axios.patch<{ ok: boolean; data?: RecurringExpense }>(
      `${API_BASE}/recurring/${id}`,
      { requesterId, ...payload }
    );
    if (!res.data?.ok || !res.data.data) {
      throw new Error('고정비 수정 응답 형식이 올바르지 않습니다.');
    }
    return res.data.data;
  } catch (err) {
    throw new Error(getErrorMessage(err, '고정비 수정에 실패했습니다.'));
  }
}
